import { fromCanonicalKg, toCanonicalKg } from '@jackedlogs/shared';
import { parseLocaleNumber } from './parseLocaleNumber';
import type { WeightUnit } from './state/loggerDraft';

// Pure plate-calculator math extracted out of LoggerScreen's
// onOpenPlatesForSet / onApplyPlateCalc (Track R). Kept apart from the
// state + dispatch in usePlateCalc so the seed/format/target rules are
// unit-testable. Structural param types so callers pass DraftSet[]
// without coupling this file to the draft module.

type WeightedSet = { weight: string; unit: WeightUnit };
type CompletableSet = { completedAt: string | null };

/**
 * Seed the calculator from the set the user is editing: take the focused
 * set's weight (canonical kg) when it has a usable value, else walk
 * backwards to the last non-zero set in the entry. Returns 0 when
 * nothing usable exists (modal opens at zero).
 */
export function seedKgFromSets(
  sets: ReadonlyArray<WeightedSet>,
  focusedIdx: number,
): number {
  const focused = sets[focusedIdx];
  if (focused) {
    const v = parseLocaleNumber(focused.weight);
    if (Number.isFinite(v) && v > 0) return toCanonicalKg(v, focused.unit);
  }
  for (let i = sets.length - 1; i >= 0; i--) {
    const candidate = sets[i];
    if (!candidate) continue;
    const v = parseLocaleNumber(candidate.weight);
    if (Number.isFinite(v) && v > 0) return toCanonicalKg(v, candidate.unit);
  }
  return 0;
}

/**
 * Convert the chosen total kg back to the set's display unit and format
 * to 2 dp as the string the reducer stores.
 */
export function formatPlateWeight(absKg: number, unit: WeightUnit): string {
  const value = fromCanonicalKg(absKg, unit);
  return (Math.round(value * 100) / 100).toString();
}

/**
 * Resolve which set apply writes to: the modal's setIdx when present,
 * else the first pending (uncompleted) set, else the last set.
 */
export function resolveApplyTargetSetIdx(
  sets: ReadonlyArray<CompletableSet>,
  setIdx: number | null,
): number {
  if (setIdx !== null) return setIdx;
  const pending = sets.findIndex((s) => s.completedAt === null);
  return pending === -1 ? sets.length - 1 : pending;
}
